/**
 * TestBridge 注册表（A 阶段：SFormCollector 的注册/去重/查询）
 *
 * 职责：
 * - registerForm：挂载点 A（useSForm）/ 挂载点 B（FormItemRow）注册采集来源，按 formId 去重（先注册者生效，§6.4）
 * - WeakMap<FormInstance, formId>：C2 修订，供 SFormItem 通过 useFormInstance() 反查 formId 注入 testId
 * - getFormState：委托 buildFormState 采集算法（A/C 共用）
 *
 * R1 一致性约束（技术方案 §14.2）：registerForm 写入 WeakMap 的 instance 须与
 * SFormItem useFormInstance() 拿到的同一引用。挂载点 A（useSForm）form 是 spread 副本
 * （useSForm.ts:33-35），业务传给 <Form form={form}> 的也是副本 → FormContext.form = 副本
 * → SFormItem useFormInstance 返回副本 → 键一致。挂载点 A/B 实施时仍须实测确认（A11/A13）。
 */

import { isTestBridgeEnabled } from './env';
import {
  FormStateSource,
  FormStateCollector,
  FormTestState,
  TestComponentSummary,
  BedrockFormInstance,
} from './types';
import { buildFormState } from './adapters/sformCollector';

/** C2：FormInstance → formId 反查映射（供 SFormItem 注入 testId，不依赖 SFormContext） */
const formInstanceToFormId: WeakMap<BedrockFormInstance, string> = new WeakMap();

/**
 * SFormItem（A12）通过 useFormInstance() 拿当前 form instance 后调本函数反查 formId。
 * 未注册（裸 Form.useForm + 直接 SFormItem 场景）返回 undefined → 不注入 testId（降级）。
 */
export const getFormIdByInstance = (
  instance: BedrockFormInstance | null | undefined,
): string | undefined => {
  if (!instance) return undefined;
  return formInstanceToFormId.get(instance as BedrockFormInstance);
};

class TestBridgeRegistry implements FormStateCollector {
  private forms: Map<string, FormStateSource> = new Map();

  registerForm(formId: string, source: FormStateSource): void {
    // 去重：先注册者生效（A 阶段双挂 useSForm + FormItemRow 同页面共存时仅一份）
    if (this.forms.has(formId)) {
      if (isTestBridgeEnabled()) {
        // eslint-disable-next-line no-console
        console.warn(
          `[test-bridge] formId "${formId}" already registered, skip (dedup: first-wins)`,
        );
      }
      return;
    }
    this.forms.set(formId, source);
    // C2：写入 instance → formId 映射，供 SFormItem 反查
    const instance = source.getInstance();
    if (instance) {
      formInstanceToFormId.set(instance as BedrockFormInstance, formId);
    }
  }

  unregisterForm(formId: string): void {
    // WeakMap 无需显式删（instance GC 后自动回收）；同 instance 重注册时 registerForm 会覆盖
    this.forms.delete(formId);
  }

  getFormState(formId: string): FormTestState | null {
    const source = this.forms.get(formId);
    if (!source) return null;
    const instance = source.getInstance();
    if (!instance) return null;
    const metas = source.getFieldMetas();
    return buildFormState(formId, instance, metas, source.getSubmitState?.());
  }

  listForms(): TestComponentSummary[] {
    const list: TestComponentSummary[] = [];
    this.forms.forEach((source, formId) => {
      const instance = source.getInstance();
      const fieldCount = instance ? instance.getFieldsError().length : 0;
      list.push({ id: formId, type: 'form', fieldCount });
    });
    return list;
  }
}

/** 单例注册表（A 阶段 SFormCollector；C 阶段切换 BedrockCollector 时替换实现） */
export const testBridgeRegistry: FormStateCollector = new TestBridgeRegistry();
